import React, { Suspense, useContext, useEffect, useState } from 'react'
import { LeagueEvents } from '../../types/events'
import DatesTableItems from '../datesTableItems/datesTableItems'
import { Nodata } from '../nodata/nodata'
import CustomImage from '../customImage/customImage'
import { IEvent, IEventLeague } from '../../api/dto/IEvent'
import { IEventWithStream, InitialStateContext } from '../../App'
import { leaguesPriority } from '../../config/leagues-priority'
import { IStream } from '../../api/dto/IStream'
import ChannelsItem from '../channelsItem/channelsItem'

interface Props {
    allEvents: IEvent[]
    currentEvent: IEventWithStream | null
    setCurrentEvent: (event: IEventWithStream) => void
    onLeagueChange?: (league: IEventLeague) => void
    initialLeagueId?: string | null
    defaultChannel: IStream | null
    onStreamClick: (stream: IStream) => void
}

export const EventsSidebar = ({
    allEvents,
    setCurrentEvent,
    defaultChannel,
    currentEvent,
    onLeagueChange,
    initialLeagueId,
    onStreamClick,
}: Props) => {
    const { currentSport } = useContext(InitialStateContext)
    const [leagues, setLeagues] = useState<LeagueEvents[]>([])
    const [currentLeague, setCurrentLeague] = useState<LeagueEvents | null>(
        null
    )
    const getSortedLeagues = (leagues: LeagueEvents[]) => {
        const priorities = leaguesPriority[currentSport]
        if (!priorities) return leagues
        return leagues.sort((a, b) => {
            const aLeague = priorities.find(
                ({ name }) => name === a.league.name
            )
            const bLeague = priorities.find(
                ({ name }) => name === b.league.name
            )
            if (!aLeague) return 1
            if (!bLeague) return -1
            return aLeague?.order - bLeague?.order
        })
    }

    const handleSetCurrentLeague = (selectedLeague: LeagueEvents) => {
        setCurrentLeague(selectedLeague)
        onLeagueChange?.(selectedLeague.league)
    }

    const getAvailableLeagues = (events: IEvent[]) => {
        const leagues: LeagueEvents[] = []

        events?.forEach((event) => {
            const eventLeague = leagues.find(
                ({ league }) => league.uuid === event.league.uuid
            )
            if (eventLeague) {
                eventLeague.events.push(event)
            } else {
                leagues.push({
                    league: event.league,
                    events: [event],
                })
            }
        })
        return leagues
    }

    const handleChannelClick = () => {
        defaultChannel && onStreamClick(defaultChannel)
    }

    useEffect(() => {
        const allLeagues = getAvailableLeagues(allEvents)

        if (initialLeagueId && allLeagues.length) {
            const initialLeague = allLeagues.find(
                (league) => league.league.uuid === initialLeagueId
            )
            initialLeague && handleSetCurrentLeague(initialLeague)
        }

        const sortedLeagues = getSortedLeagues(allLeagues)
        setLeagues(sortedLeagues)
    }, [allEvents])

    return (
        <Suspense>
            <div className="flex h-full min-h-[550px]">
                {leagues.length > 1 && (
                    <div className="flex flex-col w-1/6 lg:w-1/4 max-w-[82px] bg-darkBlueOpacity1 rounded-[16px_0_0_16px] p-2 gap-1 overflow-y-auto thinScrollbar overflow-x-hidden">
                        {leagues.map((leagueEvents) => (
                            <div
                                className={` group relative flex items-center justify-center p-3 lg:p-5 cursor-pointer rounded-[16px] hover:bg-whiteOpacity1  ${currentLeague?.league.uuid === leagueEvents.league.uuid && 'bg-whiteOpacity1'} has-tooltip`}
                                onClick={() =>
                                    handleSetCurrentLeague(leagueEvents)
                                }
                                key={leagueEvents.league.uuid}
                            >
                                <CustomImage
                                    width={25}
                                    height={25}
                                    className="w-[25px] min-w-[25px] min-h-[25px] h-[25px]"
                                    mockImg="/images/mockLogos/noTeam.svg"
                                    src={leagueEvents.league.logo}
                                    alt={leagueEvents.league.name}
                                />
                                <span className="z-50 absolute top-auto left-[4.5rem] scale-0 transition-all rounded bg-gray-800 p-2 text-xs text-white group-hover:scale-100 text-nowrap bg-blue-800 font-poppinsRegular">
                                    {leagueEvents.league.name}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex flex-col w-full overflow-y-scroll thinScrollbar">
                    {defaultChannel && (
                        <div className="m-2">
                            <ChannelsItem
                                item={defaultChannel}
                                currentEvent={currentEvent}
                                onClick={handleChannelClick}
                            />
                        </div>
                    )}
                    {allEvents?.length ? (
                        <DatesTableItems
                            allEvents={currentLeague?.events || allEvents}
                            currentEvent={currentEvent}
                            handleSetCurrentEvent={setCurrentEvent}
                        />
                    ) : (
                        <Nodata />
                    )}
                </div>
            </div>
        </Suspense>
    )
}
